import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {ENTRY_TABLE_CELL_COUNT_LIMIT, TableLimitUtils} from 'notebook/performance/tableLimitUtils';
import {TablePerformanceSummaryBar} from 'notebook/performance/TablePerformanceSummaryBar';
import {TablePerformanceSummaryTable} from 'notebook/performance/TablePerformanceSummaryTable';
import TableUtil from 'notebook/tables/TableUtil';
import {Note} from 'notebook/util/NoteUtilConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Modal from 'ui-library/modals/Modal';

interface NotebookPerformanceMonitorModalProps extends NotebookPerformanceMonitorProps {
  onRequestClose: () => void;
}

interface NotebookPerformanceMonitorProps {
  note: Note;
}

export function NotebookPerformanceMonitorModal({
  onRequestClose,
  note,
}: NotebookPerformanceMonitorModalProps): JSX.Element {
  return (
    <div
      onMouseDown={(e): void => {
        e.stopPropagation();
      }}
    >
      <Modal title="Table Performance Breakdown" onRequestClose={onRequestClose}>
        <NotebookPerformanceMonitor note={note} />
      </Modal>
    </div>
  );
}

export function NotebookPerformanceMonitor({note}: NotebookPerformanceMonitorProps): JSX.Element {
  const [focusedTableName, setFocusedTableName] = React.useState<string | null>(null);

  const tables = TableUtil.getTablesFromNote(note, {
    addRunSchemaName: true,
    removeMixtureIngredientSuffix: true,
  });

  const {numTables, numCells, tableSummaries} = TableLimitUtils.analyzeNotePerformance(tables);

  return (
    <div>
      <TablePerformanceSummaryBar
        limitThreshold={ENTRY_TABLE_CELL_COUNT_LIMIT}
        numCells={numCells}
        tableSummaries={tableSummaries}
        focusedTableName={focusedTableName}
        onHover={(tableName): void => {
          setFocusedTableName(tableName);
        }}
        onLeave={(): void => {
          setFocusedTableName(null);
        }}
      />
      {TableLimitUtils.sizeIsInWarningRange(numCells) && (
        <section className={css(styles.warningSection)}>
          <InlineBanner
            bannerType={InlineBannerType.WARNING}
            icon="gauge"
            text="Approaching table performance limit"
            supportingInfo="Adding more tables, rows, and columns may cause slowdowns"
          />
        </section>
      )}
      {TableLimitUtils.sizeIsOverMaximum(numCells) && (
        <section className={css(styles.warningSection)}>
          <InlineBanner
            bannerType={InlineBannerType.ERROR}
            icon="gauge-max"
            text="Table performance limit reached"
            supportingInfo="To resolve this, break up the entry or reduce table size"
          />
        </section>
      )}
      <div className={css(styles.metricsContainer)}>
        <div className={css(styles.metricValue)}>{numCells.toLocaleString()} table cells</div>
        <div className={css(styles.metricValue)}>{numTables.toLocaleString()} tables</div>
      </div>
      <TablePerformanceSummaryTable
        tableSummaries={tableSummaries}
        focusedTableName={focusedTableName}
        onHover={(tableName): void => {
          setFocusedTableName(tableName);
        }}
        onLeave={(): void => {
          setFocusedTableName(null);
        }}
      />
      <div>
        Check out our&nbsp;
        <a
          className={css(styles.helpLink)}
          href="https://help.benchling.com/hc/en-us/articles/35379317900557-Understanding-and-Managing-Notebook-Entry-Size"
          target="_blank"
          rel="noopener noreferrer"
        >
          <b>Help center article</b> <BenchlingIcon icon="open" />
        </a>
        &nbsp;for tips to improve performance
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  warningSection: {
    marginTop: '16px',
  },
  metricsContainer: {
    display: 'flex',
    flexDirection: 'row',
    padding: '18px 0',
  },
  metricValue: {
    fontSize: '1.5em',
    paddingRight: '24px',
  },
  helpLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
  },
});
